<template>
  <div
    class="qItem"
    :class="{ sel: selected, coldItem: muted, blockedItem: blocked, disabled }"
    :role="disabled ? undefined : 'button'"
    :tabindex="disabled ? undefined : 0"
    @click="select"
    @keydown.enter.prevent="select"
    @keydown.space.prevent="select"
  >
    <slot name="lead" />
    <span class="avatar" :style="{ background: avatarBg }"><slot name="avatar" /></span>
    <span class="qBody">
      <span class="qName">{{ name }}</span>
      <span class="qRole">{{ sub }}</span>
    </span>
    <span v-if="$slots.meta" class="qMeta" :title="metaTitle"><slot name="meta" /></span>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  name: string
  sub: string
  avatarBg: string
  selected?: boolean
  muted?: boolean
  /** Dimmer still: nothing to act on here. */
  blocked?: boolean
  /** Not a control at all: no focus, no hover, no select. */
  disabled?: boolean
  metaTitle?: string
}>()

const emit = defineEmits<{ (e: 'select'): void }>()

function select(): void {
  if (!props.disabled) emit('select')
}
</script>

<style scoped>
.qItem { display: flex; align-items: center; gap: var(--space-3); width: 100%; padding: var(--space-3) var(--space-4); border: 0; background: transparent; text-align: left; border-left: 2px solid transparent; cursor: pointer; transition: background var(--duration-fast) var(--ease-out); }
.qItem:hover { background: var(--color-bg-subtle); }
.qItem.sel { background: var(--color-bg-surface); border-left-color: var(--color-accent); box-shadow: var(--shadow-sm); }
.qItem.disabled { cursor: default; }
.qItem.disabled:hover { background: transparent; }
.avatar { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 999px; flex-shrink: 0; font-size: var(--fs-sm); font-weight: var(--fw-semibold); color: var(--color-text-primary); }
.qBody { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.qName { font-size: var(--fs-base); font-weight: var(--fw-semibold); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.qRole { font-size: var(--fs-xs); color: var(--color-text-tertiary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.coldItem .avatar { color: var(--color-text-tertiary); box-shadow: inset 0 0 0 1px var(--color-border-subtle); }
.coldItem .qName { font-weight: var(--fw-medium); color: var(--color-text-secondary); }
.blockedItem .qName { color: var(--color-text-tertiary); }

.qMeta { display: inline-flex; align-items: center; gap: 4px; flex-shrink: 0; max-width: 45%; font-size: var(--fs-xs); color: var(--color-text-tertiary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

@container rail (max-width: 120px) {
  .qItem { justify-content: center; padding: var(--space-1) 0; gap: 0; border-left: 0; }
  .qItem.sel { box-shadow: none; background: transparent; }
  .qItem.sel .avatar { box-shadow: 0 0 0 2px var(--color-accent); }
  .qBody, .qMeta { display: none; }
}
</style>
